import { css } from '@emotion/core';
import React, { forwardRef } from 'react';
import {
	color,
	layout,
	LayoutProps,
	typography,
	TypographyProps,
	space,
	SpaceProps,
	ColorProps,
} from 'styled-system';

import LoaderSpin from 'components/loaders/LoaderSpin';
import Link, { HrefLink } from 'components/styled/Link';
import { Box } from 'components/styled';

import { isChromeTranslatorActive } from 'utils';

import styled from 'theme/styled';

type ButtonProps = {
	variant?: 'default' | 'primary' | 'outlined' | 'text';
	sizeType?: 'normal' | 'big'; // size is used by 'space'
};

type Props = LayoutProps &
	SpaceProps &
	TypographyProps &
	ButtonProps &
	ColorProps;

export const resetButtonStyle = css`
	line-height: 1.5;
	background: transparent;
	border: 0;
	cursor: pointer;
`;

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const StyledButton = styled.button<Props>`
	${resetButtonStyle}

	background: ${p => p.theme.colors.secondary};
	padding: ${p => p.theme.space[3]}px;
	font-size: ${p => p.theme.fontSizes.md}px;
	text-align: center;
	display: inline-flex;
	align-items: center;
	justify-content: center;

	&:disabled {
		${p =>
			p.variant !== 'text' &&
			css`
				background: ${p.theme.colors.darkerGrey};
			`}

		${p =>
			p.variant === 'text' &&
			css`
				color: ${p.theme.colors.darkerGrey};
			`}
	}

	${p =>
		p.variant !== 'text' &&
		css`
			color: white;
			min-width: 100px;
		`}

	${p =>
		p.variant === 'primary' &&
		css`
			background: ${p.theme.colors.primary};
		`}

  	${p =>
		p.variant === 'outlined' &&
		css`
			background: transparent;
			border: 1px solid ${p.theme.colors.primary};
			color: ${p.theme.colors.primary};
		`}

  	${p =>
		p.sizeType === 'big' &&
		css`
			font-size: ${p.theme.fontSizes.xl}px;
			padding-left: ${p.theme.space[4]}px;
			padding-right: ${p.theme.space[4]}px;

			@media screen and (max-width: ${p.theme.breakpoints[0]}) {
				font-size: ${p.theme.fontSizes.lg}px;
				padding: ${p.theme.space[2]}px ${p.theme.space[3]}px;
			}
		`}

		${p =>
		p.variant === 'text' &&
		css`
			${!p.color && `color: ${p.theme.colors.secondary};`}
			background: transparent;
			border: 0;
		`}

    ${layout}
    ${space}
    ${typography}
		${color}
`;

type StyledButtonProps = React.ComponentProps<typeof StyledButton>;

StyledButton.defaultProps = {
	type: 'button',
	variant: 'default',
	sizeType: 'normal',
};

const Button = forwardRef<
	HTMLButtonElement,
	StyledButtonProps & { loading?: boolean }
>(({ children, loading, ...buttonProps }, ref) => (
	<StyledButton {...buttonProps} ref={ref}>
		{/*
			wee need to check weather the chrome translator is active to avoid crash
			issue documented here: https://github.com/facebook/react/issues/11538#issuecomment-390386520
		*/}
		{loading && !isChromeTranslatorActive() ? (
			<Box display="inline-block" mr={2}>
				<LoaderSpin
					color="white"
					size={buttonProps.sizeType === 'big' ? 25 : 18}
				/>
			</Box>
		) : (
			<></>
		)}

		{children}
	</StyledButton>
));

Button.displayName = Button.name;

export const NavButton = styled(StyledButton)`
	cursor: pointer;
`;

export const NavLinkButton = NavButton.withComponent(Link);
export const NavHrefButton = styled(NavButton)`
	text-decoration: none;
	&:hover {
		text-decoration: underline ${p => p.theme.colors.primary};
	}
`.withComponent(HrefLink);

export default Button;
